import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from './ui/dropdown-menu';
import { 
  Loader2, 
  Send, 
  MoreVertical, 
  Trash2, 
  Edit2, 
  MessageSquare,
  AlertCircle,
  CheckCircle2,
  Lock,
  Unlock,
  UserPlus
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { MentionTextarea, MentionDisplay } from './MentionTextarea';
import { toast } from 'sonner';

interface Message {
  id: string;
  content: string;
  senderId: string;
  senderName: string;
  senderRole: string;
  createdAt: string;
  type: string;
  isEdited?: boolean;
  isDeleted?: boolean;
  parentId?: string;
}

interface ContextualCommentsProps {
  companyId: string;
  contextType: string;
  contextId: string;
  currentUser: any;
  accessToken: string;
  title?: string;
}

export function ContextualComments({
  companyId,
  contextType,
  contextId,
  currentUser,
  accessToken,
  title = "Comments & Activity"
}: ContextualCommentsProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [mentionedUsers, setMentionedUsers] = useState<string[]>([]);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  
  const bottomRef = useRef<HTMLDivElement>(null);

  const fetchMessages = async () => {
    try {
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/messages?companyId=${companyId}&contextType=${contextType}&contextId=${contextId}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (e) {
      console.error("Error fetching messages", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000); // Polling for MVP
    return () => clearInterval(interval);
  }, [companyId, contextType, contextId]);

  useEffect(() => {
    if (!loading && messages.length > 0) {
      // Only scroll on initial load or new message sent by me? 
      // For now, simple scroll to bottom on load
      // bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [loading]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/messages`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          companyId,
          contextType,
          contextId,
          content: newMessage,
          senderId: currentUser.id,
          senderName: currentUser.name || currentUser.email,
          senderEmail: currentUser.email,
          senderRole: currentUser.role,
          mentions: mentionedUsers,
          type: 'comment'
        })
      });

      if (res.ok) {
        setNewMessage('');
        setMentionedUsers([]);
        fetchMessages();
        toast.success("Message sent");
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      } else {
        toast.error("Failed to send message");
      }
    } catch (e) {
      toast.error("Error sending message");
    } finally {
      setSending(false);
    }
  };

  const handleEdit = async (messageId: string) => {
    try {
      const res = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/messages/${messageId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: editContent,
          userId: currentUser.id,
          companyId
        })
      });

      if (res.ok) {
        setEditingMessageId(null);
        fetchMessages();
        toast.success("Message updated");
      }
    } catch (e) {
      toast.error("Error updating message");
    }
  };

  const handleDelete = async (messageId: string) => {
    if (!confirm("Are you sure you want to delete this message?")) return;
    try {
      const res = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/messages/${messageId}?companyId=${companyId}&userId=${currentUser.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      if (res.ok) {
        fetchMessages();
        toast.success("Message deleted");
      }
    } catch (e) {
      toast.error("Error deleting message");
    }
  };

  const getSystemIcon = (type: string) => {
    switch (type) {
      case 'assignment': return <UserPlus className="h-4 w-4 text-blue-500" />;
      case 'status_change': return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'lock': return <Lock className="h-4 w-4 text-orange-500" />;
      case 'unlock': return <Unlock className="h-4 w-4 text-green-500" />;
      case 'blocker': return <AlertCircle className="h-4 w-4 text-red-500" />;
      default: return <MessageSquare className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Card className="h-full flex flex-col border-none shadow-none">
      <div className="p-4 border-b">
        <h3 className="font-semibold flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          {title}
        </h3>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[300px] max-h-[600px]">
        {loading ? (
          <div className="flex justify-center p-4"><Loader2 className="animate-spin" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center text-muted-foreground p-8">No activity yet. Add a comment to begin.</div>
        ) : (
          messages.map((msg) => {
            const isMe = msg.senderId === currentUser.id;
            const isSystem = msg.type === 'system_event' || msg.senderId === 'system';

            if (isSystem) {
              return (
                <div key={msg.id} className="flex items-center justify-center gap-2 text-xs text-muted-foreground my-2">
                  {getSystemIcon(msg.type)}
                  <span>{msg.content}</span>
                  <span>• {formatTime(msg.createdAt)}</span>
                </div>
              );
            }

            return (
              <div key={msg.id} className={`flex gap-3 ${isMe ? 'flex-row-reverse' : ''}`}>
                <Avatar className="h-8 w-8">
                  <AvatarFallback>{msg.senderName.substring(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                
                <div className={`flex flex-col max-w-[80%] ${isMe ? 'items-end' : 'items-start'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-muted-foreground">{msg.senderName}</span>
                    <span className="text-[10px] text-muted-foreground">{formatTime(msg.createdAt)}</span>
                    {msg.isEdited && <span className="text-[10px] text-muted-foreground">(edited)</span>}
                  </div>

                  <div className={`p-3 rounded-lg text-sm ${
                    msg.isDeleted 
                      ? 'bg-muted text-muted-foreground italic' 
                      : isMe 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-muted'
                  }`}>
                    {editingMessageId === msg.id ? (
                      <div className="flex flex-col gap-2 min-w-[200px]">
                        <Textarea 
                          value={editContent} 
                          onChange={e => setEditContent(e.target.value)}
                          className="text-black dark:text-white bg-white dark:bg-black"
                        />
                        <div className="flex justify-end gap-2">
                          <Button size="xs" variant="ghost" onClick={() => setEditingMessageId(null)}>Cancel</Button>
                          <Button size="xs" onClick={() => handleEdit(msg.id)}>Save</Button>
                        </div>
                      </div>
                    ) : (
                       <MentionDisplay text={msg.content} />
                    )}
                  </div>
                  
                  {isMe && !msg.isDeleted && editingMessageId !== msg.id && (
                    <div className="mt-1">
                       <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-6 w-6">
                            <MoreVertical className="h-3 w-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            setEditingMessageId(msg.id);
                            setEditContent(msg.content);
                          }}>
                            <Edit2 className="h-3 w-3 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-red-500" onClick={() => handleDelete(msg.id)}>
                            <Trash2 className="h-3 w-3 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      <div className="p-4 border-t bg-background">
        <div className="flex gap-2">
          <div className="flex-1">
            <MentionTextarea
              value={newMessage}
              onChange={(val, mentions) => {
                setNewMessage(val);
                setMentionedUsers(mentions);
              }}
              placeholder="Write a comment... use @ to mention"
              companyId={companyId}
              accessToken={accessToken}
            />
          </div>
          <Button onClick={handleSend} disabled={sending || !newMessage.trim()} size="icon">
            {sending ? <Loader2 className="animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </Card>
  );
}